import React, { useState, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  FlaskConical,
  ZoomIn,
  ZoomOut,
  X,
  Check,
  Trash2,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Eye,
} from "lucide-react";
import SmartDropZone from "../components/SmartDropZone";
import { AIProcessingOverlay } from "../components/AIProcessingOverlay";
import { getApiBase } from "../config";
import { getToken } from "../auth";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API = getApiBase();

interface BoxResult {
  id: string;
  label: string;
  text_content: string;
  page_number: number;
  box: number[] | null;
  box_2d: number[] | null;
  score: number;
  anchor_label?: string;
  anchor_hint?: string;
  anchor_used: boolean;
  anchor_match_score?: number | null;
  grounding_status: string;
  strategy: string;
  status?: "PROPOSED" | "CONFIRMED" | "REJECTED";
}

interface MikeFinding {
  label: string;
  text_content: string;
  page_number: number;
  anchor_label?: string;
  anchor_hint?: string;
  box_2d?: number[] | null;
}

interface TemplateGroup {
  group_id: string;
  segment_sig: string;
  instance_count: number;
  representative_segment: number;
  page_range: number[];
  all_instances: { segment_index: number; start_page: number; end_page: number }[];
}

interface Stats {
  total: number;
  grounded: number;
  ungrounded: number;
  avg_score: number;
}

interface LabResponse {
  segments: any[];
  template_groups: TemplateGroup[];
  current_grounding: BoxResult[];
  anchor_first_grounding: BoxResult[];
  mike_findings: MikeFinding[];
  stats: { current: Stats; anchor_first: Stats };
  page_count: number;
  fingerprint_hash: string;
}

type Phase = "upload" | "processing" | "compare";

const GroundingLabPage: React.FC = () => {
  const [phase, setPhase] = useState<Phase>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<LabResponse | null>(null);
  const [, setNumPages] = useState(0);
  const [scale, setScale] = useState(0.7);
  const [progress, setProgress] = useState(0);
  const [subtitle, setSubtitle] = useState("Preparing...");
  const [selectedBox, setSelectedBox] = useState<BoxResult | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [findingsOpen, setFindingsOpen] = useState(true);
  const [boxStatuses, setBoxStatuses] = useState<Record<string, "CONFIRMED" | "REJECTED">>({});

  const leftRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const rightRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const syncing = useRef(false);

  const handleScroll = useCallback((source: "left" | "right") => {
    if (syncing.current) return;
    syncing.current = true;
    const from = source === "left" ? leftRef.current : rightRef.current;
    const to = source === "left" ? rightRef.current : leftRef.current;
    if (from && to) {
      to.scrollTop = from.scrollTop;
    }
    requestAnimationFrame(() => { syncing.current = false; });
  }, []);

  const handleUpload = async (selectedFile: File) => {
    setFile(selectedFile);
    setPhase("processing");
    setProgress(0);
    setSubtitle("Uploading document...");

    const subtitles = [
      "Uploading document...",
      "Running MIke AI scan...",
      "Building word maps...",
      "Current grounding pass...",
      "Anchor-first grounding pass...",
      "Computing segments...",
      "Finalizing comparison...",
    ];
    let step = 0;
    const interval = setInterval(() => {
      step = Math.min(step + 1, subtitles.length - 1);
      setSubtitle(subtitles[step]);
      setProgress(Math.min(0.95, (step + 1) / subtitles.length));
    }, 3000);

    try {
      const token = getToken();
      const form = new FormData();
      form.append("file", selectedFile);
      form.append("rules", "[]");
      form.append("context", "{}");

      const res = await fetch(`${API}/admin/grounding-lab`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      if (!res.ok) {
        const err = await res.text();
        throw new Error(err);
      }

      const result: LabResponse = await res.json();
      setData(result);
      setProgress(1);
      setSubtitle("Done!");
      setTimeout(() => setPhase("compare"), 400);
    } catch (e: any) {
      console.error("Grounding lab error:", e);
      alert(`Error: ${e.message || e}`);
      setPhase("upload");
    } finally {
      clearInterval(interval);
    }
  };

  const reset = () => {
    setPhase("upload");
    setFile(null);
    setData(null);
    setSelectedBox(null);
    setSelectedGroup(null);
    setNumPages(0);
    setBoxStatuses({});
  };

  const confirmBox = (id: string) => {
    setBoxStatuses((prev) => ({ ...prev, [id]: "CONFIRMED" }));
    setSelectedBox(null);
  };

  const rejectBox = (id: string) => {
    setBoxStatuses((prev) => ({ ...prev, [id]: "REJECTED" }));
    setSelectedBox(null);
  };

  const filterByGroup = (results: BoxResult[]) => {
    if (!selectedGroup || !data) return results;
    const group = data.template_groups.find((g) => g.group_id === selectedGroup);
    if (!group) return results;
    const [start, end] = group.page_range;
    return results.filter((r) => r.page_number >= start && r.page_number <= end);
  };

  const getVisiblePages = (): number[] => {
    if (!data) return [];
    if (!selectedGroup) {
      return Array.from({ length: data.page_count }, (_, i) => i + 1);
    }
    const group = data.template_groups.find((g) => g.group_id === selectedGroup);
    if (!group) return Array.from({ length: data.page_count }, (_, i) => i + 1);
    const [start, end] = group.page_range;
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  };

  const getBoxColor = (score: number, id: string) => {
    const status = boxStatuses[id];
    if (status === "CONFIRMED") return "border-black bg-black/60";
    if (status === "REJECTED") return "border-red-800 bg-red-900/30 opacity-40";
    if (score >= 0.9) return "border-emerald-500 bg-emerald-500/20";
    if (score >= 0.7) return "border-amber-500 bg-amber-500/20";
    return "border-red-500 bg-red-500/20";
  };

  const renderBoxes = (results: BoxResult[], pageNum: number, panelId: string) => {
    const pageBoxes = results.filter((r) => r.page_number === pageNum && r.box_2d);
    return pageBoxes.map((r) => {
      const box = r.box_2d!;
      const y = box[0] / 10;
      const x = box[1] / 10;
      const h = (box[2] - box[0]) / 10;
      const w = (box[3] - box[1]) / 10;
      const isSelected = selectedBox?.id === r.id;
      const status = boxStatuses[r.id];

      return (
        <div
          key={`${panelId}-${r.id}`}
          className={`absolute border-2 cursor-pointer transition-all ${getBoxColor(r.score, r.id)} ${isSelected ? "ring-2 ring-blue-400 ring-offset-1 z-30" : "z-10"}`}
          style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }}
          onClick={(e) => { e.stopPropagation(); setSelectedBox(r); }}
          title={`${r.label}: ${r.text_content?.slice(0, 30)}`}
        >
          {/* Action popup on selected box */}
          {isSelected && (
            <div
              className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-slate-800 text-slate-100 text-xs p-3 rounded-lg shadow-xl w-56 z-50 border border-slate-600"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-bold uppercase text-[10px] tracking-wide text-slate-500">
                    {r.grounding_status || r.strategy}
                  </p>
                  <p className="font-bold text-sm">{r.label}</p>
                  {r.text_content && (
                    <p className="text-slate-400 font-mono text-[10px] mt-0.5 truncate">{r.text_content}</p>
                  )}
                  {r.anchor_label && (
                    <p className="text-slate-500 text-[10px] mt-1">Anchor: {r.anchor_label}</p>
                  )}
                  <p className="text-[10px] mt-0.5">
                    Score: <span className={r.score >= 0.9 ? "text-emerald-400" : r.score >= 0.7 ? "text-amber-400" : "text-red-400"}>{r.score.toFixed(3)}</span>
                    {r.anchor_used && <span className="text-slate-500 ml-2">dist: {r.anchor_match_score ?? "\u2014"}</span>}
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-2">
                {status !== "CONFIRMED" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); confirmBox(r.id); }}
                    className="flex-1 flex items-center justify-center gap-1 rounded bg-green-600 py-1.5 font-bold text-white hover:bg-green-500 transition-colors"
                  >
                    <Check className="h-3 w-3" /> Confirm
                  </button>
                )}
                {status !== "REJECTED" && (
                  <button
                    onClick={(e) => { e.stopPropagation(); rejectBox(r.id); }}
                    className={`flex items-center justify-center gap-1 rounded py-1.5 font-bold transition-colors ${
                      status !== "CONFIRMED"
                        ? "flex-1 bg-slate-700 text-red-400 hover:bg-slate-600"
                        : "w-full bg-red-600 text-white hover:bg-red-500"
                    }`}
                  >
                    <Trash2 className="h-3 w-3" /> Reject
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const renderPanel = (
    label: string,
    stats: Stats,
    results: BoxResult[],
    ref: React.RefObject<HTMLDivElement>,
    scrollSource: "left" | "right",
    panelId: string,
  ) => (
    <div className="flex-1 flex flex-col min-w-0 min-h-0">
      <div className="flex items-center gap-3 text-xs px-3 py-1.5 bg-slate-800 border-b border-slate-700 shrink-0">
        <span className="font-semibold text-slate-300">{label}</span>
        <span className="text-emerald-400">{stats.grounded}</span>
        <span className="text-red-400">{stats.ungrounded}</span>
        <span className="text-slate-500">/ {stats.total}</span>
        <span className="text-blue-400 ml-auto">avg {stats.avg_score.toFixed(3)}</span>
      </div>
      <div
        ref={ref}
        className="flex-1 overflow-auto bg-slate-950 p-4"
        onScroll={() => handleScroll(scrollSource)}
        onClick={() => setSelectedBox(null)}
        style={{ minHeight: 0 }}
      >
        <div className="flex flex-col items-center">
          <Document
            file={file}
            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
            loading={<div className="text-slate-500 py-12">Loading PDF...</div>}
          >
            {visiblePages.map((pageNum) => (
              <div key={`${panelId}-${pageNum}`} className="relative mb-4 inline-block">
                <div className="absolute top-0 left-0 z-30 bg-slate-800/80 text-[10px] text-slate-400 px-1.5 py-0.5 rounded-br">
                  p{pageNum}
                </div>
                <Page
                  pageNumber={pageNum}
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="bg-white shadow-lg"
                />
                {renderBoxes(results, pageNum, panelId)}
              </div>
            ))}
          </Document>
        </div>
      </div>
    </div>
  );

  const visiblePages = getVisiblePages();
  const currentFiltered = data ? filterByGroup(data.current_grounding) : [];
  const anchorFiltered = data ? filterByGroup(data.anchor_first_grounding) : [];

  // ── UPLOAD PHASE ──
  if (phase === "upload") {
    return (
      <div className="max-w-2xl mx-auto mt-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-purple-500/10 rounded-xl">
            <FlaskConical className="w-7 h-7 text-purple-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Grounding Lab</h1>
            <p className="text-sm text-slate-400">Compare grounding strategies side by side</p>
          </div>
        </div>
        <SmartDropZone onFileSelect={handleUpload} />
      </div>
    );
  }

  // ── PROCESSING PHASE ──
  if (phase === "processing") {
    return (
      <AIProcessingOverlay
        isVisible={true}
        title="Grounding Lab"
        subtitle={subtitle}
        progress={progress}
      />
    );
  }

  // ── COMPARE PHASE ──
  if (!data || !file) return null;

  const findings = data.mike_findings || [];

  return (
    <div
      style={{
        position: "fixed",
        top: "4rem",
        left: "16rem",
        right: 0,
        bottom: 0,
        display: "flex",
        flexDirection: "column",
        zIndex: 10,
        background: "#0f172a",
      }}
    >
      {/* ── TOP BAR ── */}
      <div className="flex items-center gap-2 px-4 py-2 bg-slate-900 border-b border-slate-700 shrink-0 flex-wrap">
        <FlaskConical className="w-4 h-4 text-purple-400" />
        <span className="text-sm font-semibold text-slate-300 mr-1">Templates:</span>

        <button
          onClick={() => { setSelectedGroup(null); setSelectedBox(null); }}
          className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
            !selectedGroup ? "bg-purple-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
          }`}
        >
          All ({data.page_count}p)
        </button>

        {data.template_groups.map((g) => (
          <button
            key={g.group_id}
            onClick={() => { setSelectedGroup(g.group_id); setSelectedBox(null); }}
            className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
              selectedGroup === g.group_id ? "bg-purple-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
            }`}
          >
            Seg {g.representative_segment} ({"\u00D7"}{g.instance_count})
          </button>
        ))}

        <div className="flex-1" />

        {/* Toggle MIke panel */}
        <button
          onClick={() => setFindingsOpen((v) => !v)}
          className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors flex items-center gap-1 ${
            findingsOpen ? "bg-amber-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"
          }`}
        >
          <Eye className="w-3 h-3" /> MIke ({findings.length})
        </button>

        <button onClick={() => setScale((s) => Math.max(0.3, s - 0.1))} className="p-1 rounded hover:bg-slate-700 text-slate-400">
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="text-xs text-slate-500 w-10 text-center">{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale((s) => Math.min(2, s + 0.1))} className="p-1 rounded hover:bg-slate-700 text-slate-400">
          <ZoomIn className="w-4 h-4" />
        </button>

        <button onClick={reset} className="ml-2 px-3 py-1 rounded-lg text-xs bg-slate-800 text-slate-400 hover:bg-red-900/50 hover:text-red-400 transition-colors">
          New File
        </button>
      </div>

      {/* ── MAIN AREA: MIke panel + split PDF panels ── */}
      <div className="flex flex-1 min-h-0">
        {/* MIke Findings Sidebar */}
        {findingsOpen && (
          <div className="w-72 shrink-0 flex flex-col border-r border-slate-700 bg-slate-900">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700 shrink-0">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-xs font-semibold text-slate-300">MIke Recommendations</span>
              <span className="text-[10px] text-slate-500 ml-auto">{findings.length}</span>
            </div>
            <div className="flex-1 overflow-auto" style={{ minHeight: 0 }}>
              {findings.map((f, idx) => (
                <div
                  key={idx}
                  className="px-3 py-2 border-b border-slate-800 hover:bg-slate-800/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
                      {f.label}
                    </span>
                    <span className="text-[10px] text-slate-500">p{f.page_number}</span>
                  </div>
                  <p className="text-xs text-slate-300 font-mono mt-1 truncate">{f.text_content}</p>
                  {f.anchor_label && (
                    <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                      Anchor: {f.anchor_label}
                      {f.anchor_hint && <span className="ml-1 text-slate-600">({f.anchor_hint})</span>}
                    </p>
                  )}
                </div>
              ))}
              {findings.length === 0 && (
                <div className="px-3 py-8 text-center text-xs text-slate-500">No findings</div>
              )}
            </div>
          </div>
        )}

        {/* LEFT PDF panel */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 border-r border-slate-700">
          {renderPanel("Current Grounding", data.stats.current, currentFiltered, leftRef, "left", "L")}
        </div>

        {/* RIGHT PDF panel */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          {renderPanel("Anchor-First Grounding", data.stats.anchor_first, anchorFiltered, rightRef, "right", "R")}
        </div>
      </div>
    </div>
  );
};

export default GroundingLabPage;
